import { Container, Text } from '@pixi/react'
import { BreathingText, Ga4EventType, ga4Info, getIsXinStar, SpinePlayer } from '@slot-nx/core'
import { TextStyle } from 'pixi.js'
import { gameConfig } from '../config'
import { memo, useEffect, useMemo, useRef, useState } from 'react'
import { registerSpaceBarEvent, t, Image } from '@slot-nx/core'

interface TutorialProps {
  onpointerdown: () => void
  isVisible: boolean
}

const totalPage = 3, maxPercentage = 100, centerX = gameConfig.resolution.width / 2, centerY = gameConfig.resolution.height / 2

const tutorialTapStyle = new TextStyle({ fontFamily: 'Roboto', fontSize: 48, fontWeight: '500', lineHeight: 61, fill: '#FFFFFF', align: 'center' })
const tutorialDescStyle = new TextStyle({ fontFamily: 'Arial', fontSize: 32, fontWeight: '500', lineHeight: 36, fill: '#FFFFFF', align: 'center', wordWrap: true, wordWrapWidth: 700, breakWords: true })
const tutorialTitleStyle = new TextStyle({ fontFamily: 'Roboto', fontSize: 48, fontWeight: 'bold', lineHeight: 14, fill: '#FFF27D', align: 'center', letterSpacing: 0, dropShadow: true, dropShadowColor: '#000000', dropShadowBlur: 10, dropShadowDistance: 2, dropShadowAngle: Math.PI / 2 })

const tutorialTexts = ['tutorial_1', 'tutorial_2', 'tutorial_3']

export const Tutorial = memo((props: TutorialProps) => {
  const [isXinStarChannel] = useMemo(() => getIsXinStar(), [])
  const loadingSpineData = isXinStarChannel ? 'loadingPage_zhtw.skel' : 'loadingPage.skel'
  const loadingSpineAtlas = isXinStarChannel ? 'loadingPage_zhtw.atlas' : 'loadingPage.atlas'
  const [currentPage, setCurrentPage] = useState(1)
  const [loadingPercentage, setLoadingPercentage] = useState(0)
  const [showTap, setShowTap] = useState(false)
  const isGameInfoLoaded = true
  const startTimeRef = useRef(Date.now())

  useEffect(() => {
    const intervalMs = 9
    const timer = setInterval(() => {
      setLoadingPercentage((prev) => {
        if (prev >= 99 && !isGameInfoLoaded) return prev
        const next = Math.min(maxPercentage, prev + 1)
        if (next === maxPercentage) clearInterval(timer)
        return next
      })
    }, intervalMs)
    return () => clearInterval(timer)
  }, [isGameInfoLoaded])

  useEffect(() => {
    if (loadingPercentage !== 100) return
    const timer = setTimeout(() => setShowTap(true), 200)
    return () => clearTimeout(timer)
  }, [loadingPercentage])

  useEffect(() => {
    if (!showTap) return
    registerSpaceBarEvent(props.onpointerdown)
    const loadingTime = Date.now() - startTimeRef.current
    ga4Info.addEvent(Ga4EventType.tutorial_loading_time, { sec: `${loadingTime}` })
  }, [props.onpointerdown, showTap])

  useEffect(() => {
    const timer = setInterval(() => setCurrentPage(page => (page >= totalPage ? 1 : page + 1)), 3000)
    return () => clearInterval(timer)
  }, [])

  return (
    <Container
      visible={props.isVisible}
      eventMode="static"
      pointerdown={showTap
        ? () => {
            ga4Info.addEvent(Ga4EventType.enter_main_game)
            ga4Info.recordStayTime()
            props.onpointerdown()
          }
        : undefined}
    >
      <Image name="background" image="bg_loadingPage" width={1080} height={1920} x={centerX} y={centerY} anchor={[0.5, 0.5]} />
      {!isXinStarChannel && (
        <Image name="logo" image="Image_gameLogo" width={494} height={234} x={centerX} y={175} anchor={[0.5, 0.5]} />
      )}
      <SpinePlayer data={loadingSpineData} atlas={loadingSpineAtlas} animationStr={`p${currentPage}`} x={centerX} y={centerY - 75} />
      <Container name="info container" x={centerX} y={1400}>
        <Text name="tutorial title" text={t(`tutorial_title${currentPage}`)} x={0} y={0} anchor={0.5} style={tutorialTitleStyle} />
        <Text name="tutorial description" text={t(tutorialTexts[currentPage - 1])} x={0} y={100} anchor={0.5} style={tutorialDescStyle} />
      </Container>

      { showTap && <BreathingText text={t('tap_to_continue')} x={centerX} y={1750} style={tutorialTapStyle} />}
    </Container>
  )
})
